import { Link } from "react-router-dom";
import { Tiles } from "@/components/motion/Tiles";
import { ArrowRight, MapPin } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHeader } from "@/components/layout/PageHeader";
import { CtaBand } from "@/components/layout/CtaBand";
import { Item, Lines, Reveal, Stagger } from "@/components/motion/Reveal";

const ZONES = [
  { name: "Catania e provincia", note: "Consegna in giornata o entro 24 h", primary: true },
  { name: "Siracusa", note: "Consegna entro 24 – 48 h" },
  { name: "Messina", note: "Consegna entro 24 – 48 h" },
  { name: "Enna e Ragusa", note: "Su richiesta, con preavviso" },
];

const POINTS = [
  ["Consegna e ritiro", "Portiamo il mezzo dove ti serve e lo ritiriamo a fine noleggio, con i nostri mezzi e il nostro personale."],
  ["Orari concordati", "Consegna la mattina presto per non farti perdere ore di cantiere. Anche il sabato."],
  ["Autocarrate pronte a partire", "Le piattaforme su Iveco Daily e Isuzu si guidano con patente B: puoi anche ritirarle direttamente."],
  ["Centri storici e spazi stretti", "Conosciamo le vie di Catania e dei paesi etnei: scegliamo insieme l'accesso più comodo al cantiere."],
];

export default function Trasporto() {
  return (
    <>
      <Seo title="Trasporto mezzi in cantiere" description="Trasporto e consegna di piattaforme aeree, escavatori e minipale in cantiere a Catania, Siracusa, Messina e in tutta la Sicilia orientale." path="/servizi/trasporto" />
      <PageHeader
        eyebrow="Servizi · Trasporto"
        lines={["Lo portiamo noi,", "dove serve."]}
        description="Consegna e ritiro dei mezzi direttamente in cantiere, in tutta la Sicilia orientale. Tu indichi l'indirizzo e l'orario: al resto pensiamo noi."
        crumbs={[{ label: "Servizi" }, { label: "Trasporto" }]}
        image="/images/trasporto.jpg"
        cta={{ label: "Richiedi una consegna", to: "/contatti" }}
      />

      <section className="bg-white py-20 sm:py-28" data-testid="trasporto-zone">
        <div className="container-x grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal y={10}><p className="eyebrow flex items-center gap-3"><Tiles />Zone servite</p></Reveal>
            <Lines as="h2" lines={["Sicilia orientale,", "coperta."]} className="display mt-3 text-4xl text-ink sm:text-5xl lg:text-6xl" />
            <Reveal delay={0.2} className="mt-6 text-ink/60"><p>Partiamo da Catania. Le tempistiche indicate sono standard: per urgenze chiamaci, spesso riusciamo a fare prima.</p></Reveal>
          </div>
          <Stagger className="lg:col-span-7" gap={0.1}>
            {ZONES.map((z) => (
              <Item key={z.name} className={`flex items-center justify-between gap-6 border-b border-ink/10 py-6 ${z.primary ? "text-ink" : "text-ink/70"}`}>
                <div className="flex items-center gap-4">
                  <MapPin className={`h-5 w-5 ${z.primary ? "text-blue-600" : "text-ink/30"}`} />
                  <span className="display text-2xl sm:text-3xl">{z.name}</span>
                </div>
                <span className="text-right font-mono text-[11px] uppercase tracking-widest text-ink/50">{z.note}</span>
              </Item>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="relative overflow-hidden bg-ink py-20 text-white sm:py-28 grain" data-testid="trasporto-points">
        <div className="absolute inset-0 blueprint opacity-50" />
        <div className="container-x relative">
          <div className="grid gap-14 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Reveal y={10}><p className="eyebrow text-blue-500 flex items-center gap-3"><Tiles />Come lavoriamo</p></Reveal>
              <Lines as="h2" lines={["Il cantiere non", "aspetta. Noi", "nemmeno."]} className="display mt-3 text-4xl sm:text-5xl lg:text-6xl" />
              <Reveal delay={0.25} className="mt-8 overflow-hidden">
                <img src="/images/trasporto.jpg" alt="Carico di un miniescavatore su camion DESP" className="aspect-[4/3] w-full object-cover" />
              </Reveal>
            </div>
            <Stagger className="grid gap-10 sm:grid-cols-2 lg:col-span-7 lg:pt-16" gap={0.1}>
              {POINTS.map(([t, d], i) => (
                <Item key={t} className="border-t border-white/15 pt-6">
                  <span className="font-mono text-xs text-blue-500">0{i + 1}</span>
                  <h3 className="display mt-3 text-2xl">{t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/60">{d}</p>
                </Item>
              ))}
            </Stagger>
          </div>
          <Reveal className="mt-14">
            <Link to="/servizi/noleggio" className="btn-ghost" data-testid="trasporto-noleggio-link">Scopri le formule di noleggio <ArrowRight className="h-4 w-4" /></Link>
          </Reveal>
        </div>
      </section>
      <CtaBand title={["Dicci dove sei.", "Arriviamo noi."]} />
    </>
  );
}
